import React, { useEffect, useState, useRef } from 'react';
import { Menu, X, Globe, ChevronRight, Linkedin, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface NavbarProps {
  language: 'en' | 'vi';
  toggleLanguage: () => void;
}
const Navbar = ({
  language,
  toggleLanguage
}: NavbarProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  // Triple-tap detection for mobile portal access
  const tapCount = useRef(0);
  const tapTimeout = useRef<NodeJS.Timeout>();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isMenuOpen]);
  const translations = {
    en: {
      home: 'Home',
      about: 'About',
      experience: 'Experience',
      projects: 'Projects',
      skills: 'Skills',
      contact: 'Contact',
      lang: 'EN',
      menu: 'Menu'
    },
    vi: {
      home: 'Trang chủ',
      about: 'Giới thiệu',
      experience: 'Kinh nghiệm',
      projects: 'Dự án',
      skills: 'Kỹ năng',
      contact: 'Liên hệ',
      lang: 'VI',
      menu: 'Menu'
    }
  };
  const t = translations[language];
  const navLinks = [{
    name: t.home,
    to: 'hero'
  }, {
    name: t.about,
    to: 'about'
  }, {
    name: t.experience,
    to: 'experience'
  }, {
    name: t.projects,
    to: 'projects'
  }, {
    name: t.skills,
    to: 'skills'
  }, {
    name: t.contact,
    to: 'contact'
  }];
  const handleScrollClick = (to: string) => {
    const element = document.getElementById(to);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  // Handle triple-tap on logo for mobile portal access
  const handleLogoTap = () => {
    tapCount.current += 1;
    if (tapTimeout.current) {
      clearTimeout(tapTimeout.current);
    }
    if (tapCount.current === 3) {
      // Triple tap detected - open founder portal
      const event = new KeyboardEvent('keydown', {
        key: 'P',
        ctrlKey: true,
        shiftKey: true,
        bubbles: true
      });
      window.dispatchEvent(event);
      tapCount.current = 0;
    } else {
      // Reset after 500ms
      tapTimeout.current = setTimeout(() => {
        tapCount.current = 0;
      }, 500);
    }
  };
  return <>
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-md shadow-md py-3' : 'bg-transparent py-5'}`} data-id="element-899">
        <div className="container mx-auto px-6 flex justify-between items-center" data-id="element-900">
          {/* Brand / Logo Area */}
          <div className="flex items-center gap-4 z-50 min-w-[200px]" data-id="element-901">
            <button onClick={e => {
            e.preventDefault();
            handleLogoTap();
            handleScrollClick('hero');
          }} className="relative group" aria-label="Logo - Triple tap for owner access" data-id="element-902">
              <div className="w-10 h-10 rounded-xl overflow-hidden border-2 border-transparent group-hover:border-blue-500 transition-all duration-300 shadow-lg active:scale-95" data-id="element-903">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/oz2JDEbs664H64BHe3SMGd/Trung_Nguyen_Design.png" alt="Trung Nguyen" className="w-full h-full object-cover" style={{
                objectPosition: 'center 20%'
              }} data-id="element-904" />
              </div>
            </button>
            <div className={`hidden sm:block font-semibold text-[19px] tracking-tight transition-colors ${isScrolled ? 'text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}`} style={{
            letterSpacing: '0.01em'
          }} data-id="element-905">
              Trung Nguyen
            </div>
          </div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex items-center bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm px-3 py-2 rounded-full border border-gray-200/50 dark:border-gray-700/50 shadow-sm" data-id="element-906">
            <ul className="flex space-x-0.5" data-id="element-907">
              {navLinks.map(link => <li key={link.name} data-id="element-908">
                  <button onClick={() => handleScrollClick(link.to)} onMouseEnter={() => setActiveSection(link.to)} className={`relative px-3.5 py-2 text-sm font-medium transition-all duration-200 rounded-full whitespace-nowrap ${language === 'vi' ? 'text-[13px] px-3' : ''} ${activeSection === link.to ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-700/50'}`} data-id="element-909">
                    {link.name}
                  </button>
                </li>)}
            </ul>
          </div>

          {/* Utilities (Language + Mobile Toggle) */}
          <div className="flex items-center gap-3 z-50 min-w-[100px] justify-end" data-id="element-910">
            <button onClick={toggleLanguage} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors text-sm font-medium text-gray-700 dark:text-gray-200" aria-label="Toggle Language" data-id="element-911">
              <Globe size={16} data-id="element-912" />
              <span data-id="element-913">{t.lang}</span>
            </button>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2 text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors" aria-label="Toggle Menu" data-id="element-914">
              {isMenuOpen ? <X size={24} data-id="element-915" /> : <Menu size={24} data-id="element-916" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-917">
        {isMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeInOut'
      }} className="fixed inset-0 bg-white dark:bg-slate-950 z-40 md:hidden flex flex-col pt-24 px-6 overflow-y-auto" data-id="element-918">
            <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1,
          duration: 0.3,
          ease: 'easeOut'
        }} className="flex flex-col space-y-2" data-id="element-919">
              {navLinks.map((link, idx) => <motion.div key={link.name} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.1 + idx * 0.05,
            duration: 0.3,
            ease: 'easeOut'
          }} data-id="element-920">
                  <button onClick={() => {
              handleScrollClick(link.to);
              setIsMenuOpen(false);
            }} className={`group w-full flex items-center justify-between p-4 font-bold text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-800 hover:text-blue-600 dark:hover:text-blue-400 transition-colors ${language === 'vi' ? 'text-xl' : 'text-2xl'}`} data-id="element-921">
                    <span data-id="element-922">{link.name}</span>
                    <ChevronRight className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-blue-500" data-id="element-923" />
                  </button>
                </motion.div>)}
            </motion.div>

            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.4,
          duration: 0.3
        }} className="mt-auto mb-10" data-id="element-924">
              <p className="text-sm text-gray-500 mb-4 uppercase tracking-wider font-semibold" data-id="element-925">
                Connect
              </p>
              <div className="flex gap-4" data-id="element-926">
                <a href="https://linkedin.com/in/realtrungnguyen" target="_blank" rel="noreferrer" className="p-3 bg-gray-100 dark:bg-slate-800 rounded-full text-blue-600 hover:bg-blue-50 transition-colors" data-id="element-927">
                  <Linkedin size={24} data-id="element-928" />
                </a>
                <a href="mailto:officialtrungnguyen@gmail.com" className="p-3 bg-gray-100 dark:bg-slate-800 rounded-full text-red-500 hover:bg-red-50 transition-colors" data-id="element-929">
                  <Mail size={24} data-id="element-930" />
                </a>
              </div>

              {/* Mobile Portal Access Hint */}
              <div className="mt-6 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800" data-id="element-931">
                <p className="text-xs text-red-600 dark:text-red-400 font-medium" data-id="element-932">
                  🔒 Owner Access: Triple-tap the logo
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
};
export default Navbar;